<template>
    <div data-testid="percentage-rollout">
        <!-- No Rollout (Draft state) — hidden in read-only mode -->
        <div
            v-if="
                (isPublished && !hasRollout && !readOnly) ||
                (!isPublished && !readOnly)
            "
            data-testid="rollout-draft"
            class="rounded-lg border border-taupe-200 bg-white p-5"
        >
            <div>
                <div class="flex items-center space-x-3">
                    <div>
                        <h3 class="text-lg font-medium text-taupe-900">
                            Percentage Rollout
                        </h3>
                    </div>
                    <div class="flex items-center space-x-2">
                        <zen-button
                            v-if="isPublished && !showConfigure"
                            type="button"
                            variant="outline-primary"
                            icon-only
                            data-testid="enable-rollout-checkbox"
                            aria-label="Enable percentage rollout"
                            @click="showConfigure = true"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="h-4 w-4"
                            >
                                <path
                                    stroke="none"
                                    d="M0 0h24v24H0z"
                                    fill="none"
                                />
                                <path d="M12 5l0 14" />
                                <path d="M5 12l14 0" />
                            </svg>
                        </zen-button>
                        <zen-button
                            v-else-if="isPublished && showConfigure"
                            type="button"
                            variant="outline-danger"
                            icon-only
                            data-testid="enable-rollout-checkbox"
                            aria-label="Disable percentage rollout"
                            @click="showConfigure = false"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="h-4 w-4"
                            >
                                <path
                                    stroke="none"
                                    d="M0 0h24v24H0z"
                                    fill="none"
                                />
                                <path d="M5 12l14 0" />
                            </svg>
                        </zen-button>
                        <zen-button
                            v-else-if="!isPublished && !draftRolloutEnabled"
                            type="button"
                            variant="outline-primary"
                            icon-only
                            data-testid="enable-rollout-checkbox"
                            aria-label="Enable percentage rollout"
                            @click="draftRolloutEnabled = true"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="h-4 w-4"
                            >
                                <path
                                    stroke="none"
                                    d="M0 0h24v24H0z"
                                    fill="none"
                                />
                                <path d="M12 5l0 14" />
                                <path d="M5 12l14 0" />
                            </svg>
                        </zen-button>
                        <zen-button
                            v-else-if="!isPublished && draftRolloutEnabled"
                            type="button"
                            variant="outline-danger"
                            icon-only
                            data-testid="enable-rollout-checkbox"
                            aria-label="Disable percentage rollout"
                            @click="draftRolloutEnabled = false"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="h-4 w-4"
                            >
                                <path
                                    stroke="none"
                                    d="M0 0h24v24H0z"
                                    fill="none"
                                />
                                <path d="M5 12l14 0" />
                            </svg>
                        </zen-button>
                    </div>
                </div>
                <p class="mt-1 text-sm text-taupe-500">
                    Gradually roll out this flag to a percentage of your users.
                </p>
            </div>

            <!-- Configure panel -->
            <div
                v-if="
                    (isPublished ? showConfigure : draftRolloutEnabled) &&
                    !readOnly
                "
                data-testid="configure-panel"
                class="mt-5 rounded-lg border border-taupe-200 bg-white p-5 space-y-4"
            >
                <div>
                    <label
                        class="block text-sm font-medium text-taupe-700"
                        for="rollout-mode"
                    >
                        Rollout Mode
                    </label>
                    <select
                        id="rollout-mode"
                        v-model="form.mode"
                        data-testid="rollout-mode-select"
                        class="mt-1 block w-full rounded-md border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                    >
                        <option value="manual">Manual</option>
                        <option value="automatic">Automatic</option>
                    </select>
                </div>

                <div v-if="form.mode === 'manual'">
                    <label
                        class="block text-sm font-medium text-taupe-700"
                        for="target-percentage"
                    >
                        Target Percentage
                    </label>
                    <div class="mt-1 flex items-center space-x-3">
                        <input
                            id="target-percentage"
                            v-model.number="form.percentage"
                            type="range"
                            min="1"
                            max="100"
                            step="1"
                            data-testid="target-percentage-slider"
                            class="h-2 w-full cursor-pointer appearance-none rounded-lg bg-taupe-200 accent-sky-600"
                        />
                        <span
                            class="w-12 text-right text-sm font-medium text-taupe-900"
                            data-testid="target-percentage-display"
                        >
                            {{ form.percentage }}%
                        </span>
                    </div>
                </div>

                <div v-if="isPublished" class="flex items-center space-x-3">
                    <zen-button
                        type="button"
                        variant="primary"
                        :processing="saving"
                        processing-label="Creating..."
                        data-testid="create-rollout-btn"
                        @click="createRollout"
                    >
                        Create Rollout
                    </zen-button>
                    <zen-button
                        type="button"
                        variant="secondary"
                        data-testid="cancel-configure-btn"
                        @click="showConfigure = false"
                    >
                        Cancel
                    </zen-button>
                </div>

                <p v-else class="text-sm text-taupe-600">
                    These rollout settings are saved when you publish or
                    schedule the draft.
                </p>
            </div>
        </div>

        <!-- Active Rollout - Manual -->
        <div
            v-if="
                hasRollout &&
                rollout.mode === 'manual' &&
                rollout.status === 'active'
            "
            data-testid="rollout-manual-active"
            class="rounded-lg border border-taupe-200 bg-white p-5 space-y-4 overflow-hidden"
        >
            <div
                class="-mx-5 -mt-5 mb-5 bg-taupe-50 border-b border-taupe-200 px-5 py-4 flex items-center justify-between"
            >
                <div>
                    <h2 class="text-xl font-medium text-taupe-800">
                        Percentage Rollout
                        <span
                            v-if="isPublished"
                            class="ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
                            data-testid="rollout-status-badge"
                        >
                            Active
                        </span>
                    </h2>
                    <p class="mt-1 text-sm text-taupe-500">
                        {{ isPublished ? 'Manual mode' : 'Manual draft' }}
                    </p>
                </div>
            </div>

            <div
                v-if="showActiveRolloutSummary"
                class="rounded-lg border border-sky-200 bg-sky-50 p-4"
                data-testid="rollout-summary"
            >
                <div class="space-y-3">
                    <div class="flex items-center space-x-3">
                        <span class="text-sm text-taupe-600"
                            >Target Value:</span
                        >
                        <span
                            class="font-mono text-sm font-medium text-taupe-900 break-all"
                            data-testid="rollout-target-value"
                        >
                            {{ activeRolloutValue ?? '(empty)' }}
                        </span>
                    </div>
                    <div v-if="hasActiveRolloutRules">
                        <span class="text-sm text-taupe-600"
                            >Rule changes:</span
                        >
                        <ul class="mt-1 space-y-1">
                            <li
                                v-for="rule in activeRolloutRules"
                                :key="rule.ulid"
                                class="text-sm text-taupe-700 pl-3 border-l-2 border-sky-300"
                            >
                                {{ rule.description || 'Rule' }}
                                →
                                {{ rule.value?.data?.display || '(value)' }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- Progress bar -->
            <div>
                <div class="flex items-center justify-between text-sm mb-1">
                    <span class="text-taupe-600">
                        {{
                            isPublished
                                ? 'Current rollout'
                                : 'Configured rollout'
                        }}
                    </span>
                    <span
                        class="font-medium text-taupe-900"
                        data-testid="current-percentage"
                    >
                        {{ rollout.percentage }}%
                    </span>
                </div>
                <div
                    class="h-3 w-full overflow-hidden rounded-full bg-taupe-200"
                >
                    <div
                        class="h-3 rounded-full bg-sky-500 transition-all duration-500"
                        :style="{ width: rollout.percentage + '%' }"
                        data-testid="progress-bar"
                    ></div>
                </div>
            </div>

            <!-- Slider to update -->
            <div v-if="!readOnly">
                <label
                    class="block text-sm font-medium text-taupe-700"
                    for="update-percentage"
                >
                    Update Percentage
                </label>
                <div class="mt-1 flex items-center space-x-3">
                    <input
                        id="update-percentage"
                        v-model.number="updateForm.percentage"
                        type="range"
                        min="1"
                        max="100"
                        step="1"
                        data-testid="update-percentage-slider"
                        class="h-2 w-full cursor-pointer appearance-none rounded-lg bg-taupe-200 accent-sky-600"
                    />
                    <span
                        class="w-12 text-right text-sm font-medium text-taupe-900"
                        data-testid="update-percentage-display"
                    >
                        {{ updateForm.percentage }}%
                    </span>
                    <zen-button
                        type="button"
                        variant="primary"
                        size="small"
                        :processing="saving"
                        data-testid="update-rollout-btn"
                        @click="updateRollout"
                    >
                        Update
                    </zen-button>
                </div>
            </div>

            <!-- Action buttons -->
            <div
                v-if="!readOnly"
                class="flex items-center space-x-3 border-t border-taupe-200 pt-4"
            >
                <zen-button
                    v-if="isPublished"
                    type="button"
                    variant="outline-warning"
                    :disabled="saving"
                    data-testid="pause-rollout-btn"
                    @click="pauseRollout"
                >
                    Pause
                </zen-button>
                <zen-button
                    v-if="isPublished"
                    type="button"
                    variant="outline-success"
                    :disabled="saving"
                    data-testid="complete-rollout-btn"
                    @click="completeRollout"
                >
                    Complete
                </zen-button>
            </div>
        </div>

        <!-- Active Rollout - Automatic -->
        <div
            v-if="
                hasRollout &&
                rollout.mode === 'automatic' &&
                rollout.status === 'active'
            "
            data-testid="rollout-automatic-active"
            class="rounded-lg border border-taupe-200 bg-white p-5 space-y-4 overflow-hidden"
        >
            <div
                class="-mx-5 -mt-5 mb-5 bg-taupe-50 border-b border-taupe-200 px-5 py-4 flex items-center justify-between"
            >
                <div>
                    <h2 class="text-xl font-medium text-taupe-800">
                        Percentage Rollout
                        <span
                            v-if="isPublished"
                            class="ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
                            data-testid="rollout-status-badge"
                        >
                            Active
                        </span>
                    </h2>
                    <p class="mt-1 text-sm text-taupe-500">
                        {{ isPublished ? 'Automatic mode' : 'Automatic draft' }}
                    </p>
                </div>
            </div>

            <div
                v-if="showActiveRolloutSummary"
                class="rounded-lg border border-sky-200 bg-sky-50 p-4"
                data-testid="rollout-summary"
            >
                <div class="space-y-3">
                    <div class="flex items-center space-x-3">
                        <span class="text-sm text-taupe-600"
                            >Target Value:</span
                        >
                        <span
                            class="font-mono text-sm font-medium text-taupe-900 break-all"
                            data-testid="rollout-target-value"
                        >
                            {{ activeRolloutValue ?? '(empty)' }}
                        </span>
                    </div>
                    <div v-if="hasActiveRolloutRules">
                        <span class="text-sm text-taupe-600"
                            >Rule changes:</span
                        >
                        <ul class="mt-1 space-y-1">
                            <li
                                v-for="rule in activeRolloutRules"
                                :key="rule.ulid"
                                class="text-sm text-taupe-700 pl-3 border-l-2 border-sky-300"
                            >
                                {{ rule.description || 'Rule' }}
                                →
                                {{ rule.value?.data?.display || '(value)' }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- 7-step stage indicator -->
            <div data-testid="stage-indicator" class="py-2">
                <div class="relative">
                    <div class="absolute inset-x-4 top-4 z-0">
                        <div class="flex items-center justify-between px-4">
                            <div
                                v-for="(_, index) in stages.length - 1"
                                :key="'line-' + index"
                                :data-testid="'stage-line-' + stages[index + 1]"
                                class="h-0.5 flex-1 mx-1 transition-all duration-300"
                                :class="
                                    isConnectorComplete(index)
                                        ? 'bg-sky-500'
                                        : 'bg-taupe-200'
                                "
                            ></div>
                        </div>
                    </div>

                    <div
                        class="relative z-10 flex items-center justify-between"
                    >
                        <div
                            v-for="(stage, index) in stages"
                            :key="stage"
                            class="flex flex-col items-center"
                            :data-testid="'stage-' + stage"
                        >
                            <div
                                class="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-all duration-300"
                                :class="stageClass(stage, index)"
                            >
                                <svg
                                    v-if="
                                        shouldShowStageCheckmark(stage, index)
                                    "
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    class="h-4 w-4"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                <span
                                    v-else
                                    :data-testid="'stage-label-' + stage"
                                >
                                    {{ stage }}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Current stage info -->
            <div
                v-if="isPublished"
                class="flex items-center justify-between rounded-lg bg-taupe-50 px-4 py-3"
            >
                <div>
                    <span class="text-sm text-taupe-600">Current stage:</span>
                    <span
                        class="ml-1 font-medium text-taupe-900"
                        data-testid="current-stage"
                    >
                        {{ rollout.percentage }}%
                    </span>
                </div>
                <div v-if="rollout.next_advance_at">
                    <span class="text-sm text-taupe-600">Next advance:</span>
                    <span
                        class="ml-1 text-sm font-medium text-sky-600"
                        data-testid="next-advance-time"
                    >
                        <zen-date-time :value="rollout.next_advance_at" />
                    </span>
                </div>
            </div>

            <!-- Action buttons -->
            <div
                v-if="!readOnly"
                class="flex items-center space-x-3 border-t border-taupe-200 pt-4"
            >
                <zen-button
                    v-if="isPublished"
                    type="button"
                    variant="outline-warning"
                    :disabled="saving"
                    data-testid="pause-rollout-btn"
                    @click="pauseRollout"
                >
                    Pause
                </zen-button>
                <zen-button
                    v-if="isPublished"
                    type="button"
                    variant="outline-success"
                    :disabled="saving"
                    data-testid="complete-rollout-btn"
                    @click="completeRollout"
                >
                    Complete
                </zen-button>
            </div>
        </div>

        <!-- Paused Rollout -->
        <div
            v-if="hasRollout && rollout.status === 'paused'"
            data-testid="rollout-paused"
            class="rounded-lg border border-amber-200 bg-amber-50 p-5 space-y-4 overflow-hidden"
        >
            <div
                class="-mx-5 -mt-5 mb-5 bg-amber-100 border-b border-amber-200 px-5 py-4 flex items-center justify-between"
            >
                <div>
                    <h2 class="text-xl font-medium text-taupe-800">
                        Percentage Rollout
                        <span
                            class="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"
                            data-testid="rollout-status-badge"
                        >
                            Paused
                        </span>
                    </h2>
                    <p class="mt-1 text-sm text-taupe-500">
                        {{ rollout.mode === 'manual' ? 'Manual' : 'Automatic' }}
                        mode &middot; {{ rollout.percentage }}%
                    </p>
                </div>
            </div>

            <div
                v-if="showActiveRolloutSummary"
                class="rounded-lg border border-sky-200 bg-sky-50 p-4"
                data-testid="rollout-summary"
            >
                <div class="space-y-3">
                    <div class="flex items-center space-x-3">
                        <span class="text-sm text-taupe-600"
                            >Target Value:</span
                        >
                        <span
                            class="font-mono text-sm font-medium text-taupe-900 break-all"
                            data-testid="rollout-target-value"
                        >
                            {{ activeRolloutValue ?? '(empty)' }}
                        </span>
                    </div>
                    <div v-if="hasActiveRolloutRules">
                        <span class="text-sm text-taupe-600"
                            >Rule changes:</span
                        >
                        <ul class="mt-1 space-y-1">
                            <li
                                v-for="rule in activeRolloutRules"
                                :key="rule.ulid"
                                class="text-sm text-taupe-700 pl-3 border-l-2 border-sky-300"
                            >
                                {{ rule.description || 'Rule' }}
                                →
                                {{ rule.value?.data?.display || '(value)' }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <p class="text-sm text-amber-800">
                This rollout is currently paused. While paused, requests are
                served the fallback value.
            </p>

            <div
                v-if="!readOnly"
                class="flex items-center space-x-3 border-t border-amber-200 pt-4"
            >
                <zen-button
                    type="button"
                    variant="outline-primary"
                    :disabled="saving"
                    data-testid="resume-rollout-btn"
                    @click="resumeRollout"
                >
                    Resume
                </zen-button>
                <zen-button
                    type="button"
                    variant="outline-success"
                    :disabled="saving"
                    data-testid="complete-rollout-btn"
                    @click="completeRollout"
                >
                    Complete
                </zen-button>
            </div>
        </div>

        <!-- Completed Rollout -->
        <div
            v-if="hasRollout && rollout.status === 'completed'"
            data-testid="rollout-completed"
            class="rounded-lg border border-green-200 bg-green-50 p-5 space-y-3 overflow-hidden"
        >
            <div
                class="-mx-5 -mt-5 mb-5 bg-taupe-50 border-b border-green-200 px-5 py-4 flex items-center justify-between"
            >
                <div>
                    <h2 class="text-xl font-medium text-taupe-800">
                        Percentage Rollout
                        <span
                            class="ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
                            data-testid="rollout-status-badge"
                        >
                            Completed
                        </span>
                    </h2>
                </div>
            </div>
            <p class="text-sm text-green-800">
                This rollout completed at
                <span class="font-medium" data-testid="completed-timestamp">
                    <zen-date-time :value="rollout.completed_at" /> </span
                >. All users are now receiving the target value.
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

import PercentageRolloutService from '@/services/percentage-rollout.service'
import { errorMessage, successMessage } from '@/utils/notifications'

const props = defineProps({
    projectKey: { type: String, required: true },
    environmentKey: { type: String, required: true },
    targetUlid: { type: String, required: true },
    /** Current rollout data from the target, if any. */
    rolloutData: { type: Object, default: null },
    /** Whether this rollout belongs to a published target. */
    isPublished: { type: Boolean, default: true },
    /** When true the component is display-only (no create / edit / action buttons). */
    readOnly: { type: Boolean, default: false },
    /** Optional published rollout summary value shown in active/paused states. */
    activeRolloutValue: { type: String, default: null },
    /** Optional published rollout summary rules shown in active/paused states. */
    activeRolloutRules: { type: Array, default: () => [] },
})

const emit = defineEmits(['updated', 'draft-rollout-change'])

/** Automatic rollout stages */
const stages = [1, 2, 5, 10, 20, 50, 100]

/** Local state */
const saving = ref(false)
const showConfigure = ref(false)
const draftRolloutEnabled = ref(false)
const rollout = ref(null)

const form = ref({
    mode: 'manual',
    percentage: 10,
})

const updateForm = ref({
    percentage: 1,
})

/** Computed */
const hasRollout = computed(() => rollout.value !== null)
const hasActiveRolloutRules = computed(
    () => (props.activeRolloutRules?.length ?? 0) > 0
)
const showActiveRolloutSummary = computed(
    () =>
        props.isPublished &&
        hasRollout.value &&
        ['active', 'paused'].includes(rollout.value?.status) &&
        (props.activeRolloutValue !== null || hasActiveRolloutRules.value)
)

/** Watchers */
watch(
    () => props.rolloutData,
    (data) => {
        if (!props.isPublished) {
            draftRolloutEnabled.value = data !== null
        }

        if (data) {
            rollout.value = { ...data }
            if (!props.isPublished) {
                const nextMode = data.mode ?? 'manual'
                const nextPercentage = Math.max(1, data.percentage ?? 10)

                if (form.value.mode !== nextMode) {
                    form.value.mode = nextMode
                }

                if (form.value.percentage !== nextPercentage) {
                    form.value.percentage = nextPercentage
                }
            }
            updateForm.value.percentage = Math.max(1, data.percentage ?? 1)
        } else {
            rollout.value = null
        }
    },
    { immediate: true }
)

watch(
    () => draftRolloutEnabled.value,
    () => {
        if (!props.isPublished) {
            emitDraftRolloutChange()
        }
    }
)

watch(
    () => form.value,
    () => {
        if (!props.isPublished) {
            emitDraftRolloutChange()
        }
    },
    { deep: true }
)

/** Methods */

function isEquivalentDraftRollout(currentRollout, nextRollout) {
    if (currentRollout === nextRollout) {
        return true
    }

    if (!currentRollout || !nextRollout) {
        return false
    }

    return (
        currentRollout.mode === nextRollout.mode &&
        currentRollout.percentage === nextRollout.percentage &&
        currentRollout.status === nextRollout.status &&
        currentRollout.stage === nextRollout.stage &&
        currentRollout.started_at === nextRollout.started_at &&
        currentRollout.paused_at === nextRollout.paused_at &&
        currentRollout.completed_at === nextRollout.completed_at &&
        currentRollout.next_advance_at === nextRollout.next_advance_at
    )
}

function emitDraftRolloutChange() {
    if (draftRolloutEnabled.value) {
        const nextDraftRollout = {
            mode: form.value.mode,
            percentage:
                form.value.mode === 'manual'
                    ? Math.max(1, form.value.percentage)
                    : 1,
            status: null,
            stage: null,
            started_at: null,
            paused_at: null,
            completed_at: null,
            next_advance_at: null,
        }

        if (isEquivalentDraftRollout(props.rolloutData, nextDraftRollout)) {
            return
        }

        emit('draft-rollout-change', nextDraftRollout)

        return
    }

    if (props.rolloutData !== null) {
        emit('draft-rollout-change', null)
    }
}

async function createRollout() {
    saving.value = true
    try {
        const response = await PercentageRolloutService.create(
            props.projectKey,
            props.environmentKey,
            props.targetUlid,
            {
                mode: form.value.mode,
                percentage:
                    form.value.mode === 'manual'
                        ? form.value.percentage
                        : undefined,
            }
        )
        rollout.value = response.data ?? response
        updateForm.value.percentage = rollout.value.percentage ?? 0
        showConfigure.value = false
        successMessage('Percentage rollout has been created.')
        emit('updated', rollout.value)
    } catch (error) {
        errorMessage(error)
    } finally {
        saving.value = false
    }
}

async function updateRollout() {
    saving.value = true
    try {
        const percentage = Math.max(1, updateForm.value.percentage)
        const response = await PercentageRolloutService.update(
            props.projectKey,
            props.environmentKey,
            props.targetUlid,
            { percentage }
        )
        rollout.value = response.data ?? response
        successMessage('Rollout percentage has been updated.')
        emit('updated', rollout.value)
    } catch (error) {
        errorMessage(error)
    } finally {
        saving.value = false
    }
}

async function pauseRollout() {
    saving.value = true
    try {
        const response = await PercentageRolloutService.update(
            props.projectKey,
            props.environmentKey,
            props.targetUlid,
            { action: 'pause' }
        )
        rollout.value = response.data ?? response
        successMessage('Rollout has been paused.')
        emit('updated', rollout.value)
    } catch (error) {
        errorMessage(error)
    } finally {
        saving.value = false
    }
}

async function resumeRollout() {
    saving.value = true
    try {
        const response = await PercentageRolloutService.update(
            props.projectKey,
            props.environmentKey,
            props.targetUlid,
            { action: 'resume' }
        )
        rollout.value = response.data ?? response
        successMessage('Rollout has been resumed.')
        emit('updated', rollout.value)
    } catch (error) {
        errorMessage(error)
    } finally {
        saving.value = false
    }
}

async function completeRollout() {
    saving.value = true
    try {
        const response = await PercentageRolloutService.complete(
            props.projectKey,
            props.environmentKey,
            props.targetUlid
        )
        rollout.value = response.data ?? response
        successMessage('Rollout has been completed.')
        emit('updated', rollout.value, response.meta?.expired_targets ?? [])
    } catch (error) {
        errorMessage(error)
    } finally {
        saving.value = false
    }
}

function isStageComplete(stage) {
    return (
        props.isPublished && rollout.value && rollout.value.percentage >= stage
    )
}

function shouldShowStageCheckmark(stage, index) {
    if (!isStageComplete(stage)) return false
    if (isCurrentStage(stage, index)) return false
    return true
}

function isCurrentStage(stage, index) {
    if (!props.isPublished || !rollout.value) return false
    const nextStage = stages[index + 1]
    return (
        rollout.value.percentage >= stage &&
        (!nextStage || rollout.value.percentage < nextStage)
    )
}

function isConnectorComplete(index) {
    if (!rollout.value) return false
    const nextStage = stages[index + 1]
    return rollout.value.percentage >= nextStage
}

function stageClass(stage, index) {
    if (isStageComplete(stage) && !isCurrentStage(stage, index)) {
        return 'bg-sky-500 text-white'
    }
    if (isCurrentStage(stage, index)) {
        return 'bg-sky-600 text-white ring-2 ring-sky-300 ring-offset-2'
    }
    return 'bg-taupe-100 text-taupe-500'
}
</script>
